import React, { useState, useMemo } from 'react';
import useMountEffect from '@restart/hooks/useMountEffect';
import classNames from 'classnames/bind';
import { useParams, useNavigate, Link } from 'react-router-dom';
import moment from 'moment';
import { DownloadOutlined } from '@ant-design/icons';
import { Space, message, Popover, QRCode, Typography, Divider, Button, Modal, Table } from 'antd';

import API from 'helpers/API';
import { phoneNumber } from 'utils/Formatter';
import { getOrderDisplayNo, getOrderStatus, getTankUseStatus, getTankChargeStatus } from 'utils/CodeMapper';
import { BottomButtons, HeadingH1, UnitTable } from 'components/ui';
import { buildTankAddModalColumns } from 'pages/tanks/tankAddModalColumns';

import styles from './tanks.module.scss';

const cx = classNames.bind(styles);

const sortText = (a, b) => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  return String(a).localeCompare(String(b));
};

const Page = () => {
  const { rid } = useParams();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [order, setOrder] = useState();
  const [tanks, setTanks] = useState([]);
  const [orderTanks, setOrderTanks] = useState();

  const getTanks = () => {
    API.get('/tanks').then(({ success, data }) => {
      setTanks(success ? data.tanks : []);
    });
  };

  const selectTank = (tank) => {
    if (tank) {
      API.post(`/orders/${rid}/tanks/${tank.rid}`).then(({ success, data }) => {
        if (success) {
          message.success({
            key: 'addNewTank',
            content: '추가 완료되었습니다.',
          });
          setOrderTanks([...orderTanks, { tank, createdAt: moment().format('YYYY-MM-DD HH:mm:ss') }]);
          getTanks();
        } else {
          message.error('할당 가능한 용기가 아닙니다.');
        }
      });
    }
  };

  const deselectTank = (item) => {
    API.delete(`/orders/${rid}/tanks/${item.tank.rid}`).then(({ success }) => {
      if (success) {
        message.success('제거 완료 되었습니다.');
        setOrderTanks(orderTanks.filter((orderTank) => orderTank.tank.rid !== item.tank.rid));
        getTanks();
      }
    });
  };

  const filteredTanks = useMemo(() => {
    return tanks.filter((data) => data.useStatus === 'READY' && data.chargeStatus === 'CHARGED');
  }, [tanks]);

  const downloadQRCode = (id) => {
    const canvas = document.getElementById(id)?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL();
      const a = document.createElement('a');
      a.download = `${id}_QRCode.png`;
      a.href = url;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  useMountEffect(() => {
    // API: 주문 상세 조회
    API.get(`/orders/${rid}`).then(({ success, data }) => {
      if (success) {
        setOrder(data);
        setOrderTanks(data.orderTanks);
      }
    });
  }, [rid]);

  useMountEffect(() => {
    getTanks();
  }, [rid]);

  return (
    <>
      <div className={cx('container')}>
        <Space direction="vertical" size={16} className={cx('wrapper')}>
          <HeadingH1 title={'용기 관리'} />

          <Typography.Text strong className={cx('title')}>
            주문정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridFour')}>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>주문번호</span>
                <span className={cx('metaValue')}>{getOrderDisplayNo(order)}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>주문일자</span>
                <span className={cx('metaValue')}>{moment(order?.orderDate, 'YYYYMMDD').format('YYYY-MM-DD')}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>상태</span>
                <span className={cx('metaValue')}>{getOrderStatus(order?.status)}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>수량</span>
                <span className={cx('metaValue')}>{order?.quantity ?? '-'}</span>
              </div>
            </div>
            <div className={cx('infoGridThree')}>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>성명</span>
                <span className={cx('metaValue')}>{order?.customerName ?? '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>회사명</span>
                <span className={cx('metaValue')}>{order?.customerCompany ?? '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>전화번호</span>
                <span className={cx('metaValue')}>{phoneNumber(order?.customerPhone) || '-'}</span>
              </div>
            </div>
          </div>

          <Divider />

          <Typography.Text strong className={cx('title')}>
            배송정보
          </Typography.Text>
          <div className={cx('sectionPanel')}>
            <div className={cx('infoGridThree')}>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>성명</span>
                <span className={cx('metaValue')}>{order?.deliveryName ?? '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>회사명</span>
                <span className={cx('metaValue')}>{order?.deliveryCompany ?? '-'}</span>
              </div>
              <div className={cx('metaCard')}>
                <span className={cx('metaLabel')}>전화번호</span>
                <span className={cx('metaValue')}>{phoneNumber(order?.deliveryPhone) || '-'}</span>
              </div>
            </div>
          </div>

          <Divider />

          <Typography.Text strong className={cx('title')}>
            용기내역
          </Typography.Text>
          <div className={cx('detailCard')}>
            <div className={cx('detailCardHeader')}>
              <Typography.Text strong>용기내역</Typography.Text>
              <Button
                disabled={order?.status === 'DELIVERED' || order?.status === 'SHIPPING'}
                type="primary"
                onClick={() => setIsOpen((isPrev) => !isPrev)}>
                용기 추가
              </Button>
            </div>
            <UnitTable
              dataSource={orderTanks}
              rowKey={(rowData) => rowData.tank.rid}
              columns={[
                {
                  key: 'qrNo',
                  dataIndex: 'qrNo',
                  title: 'QR 번호',
                  width: 140,
                  ellipsis: true,
                  defaultSortOrder: 'ascend',
                  sorter: (a, b) => sortText(a.tank?.qrcode, b.tank?.qrcode),
                  render: (_, record) => record.tank?.qrcode ?? '-',
                },
                {
                  key: 'serialNo',
                  dataIndex: 'serialNo',
                  title: '용기 일련번호',
                  width: 140,
                  ellipsis: true,
                  sorter: (a, b) => sortText(a.tank?.serialNo, b.tank?.serialNo),
                  render: (_, record) => <Link to={`/tanks/${record.tank?.rid}`}>{record.tank?.serialNo ?? '-'}</Link>,
                },
                {
                  key: 'qrcode',
                  dataIndex: 'qrcode',
                  title: 'QR코드',
                  width: 84,
                  align: 'center',
                  render: (_, record) => {
                    const qrId = `${record?.tank?.rid}_QR`;
                    return (
                      <Popover
                        id={qrId}
                        overlayInnerStyle={{ padding: 0 }}
                        content={<QRCode size={200} value={record.tank?.qrcode} bordered={false} />}>
                        <Button onClick={() => downloadQRCode(qrId)} icon={<DownloadOutlined />} />
                      </Popover>
                    );
                  },
                },
                {
                  key: 'useStatus',
                  dataIndex: 'useStatus',
                  title: '가용상태',
                  width: 76,
                  align: 'center',
                  sorter: (a, b) => sortText(getTankUseStatus(a.tank?.useStatus), getTankUseStatus(b.tank?.useStatus)),
                  render: (_, record) => getTankUseStatus(record.tank?.useStatus),
                },
                {
                  key: 'chargeStatus',
                  dataIndex: 'chargeStatus',
                  title: '충전상태',
                  width: 76,
                  align: 'center',
                  sorter: (a, b) =>
                    sortText(getTankChargeStatus(a.tank?.chargeStatus), getTankChargeStatus(b.tank?.chargeStatus)),
                  render: (_, record) => getTankChargeStatus(record.tank?.chargeStatus),
                },
                {
                  key: 'chargeCount',
                  dataIndex: 'chargeCount',
                  title: '충전횟수',
                  width: 76,
                  align: 'right',
                  sorter: (a, b) => Number(a.tank?.chargeCount ?? 0) - Number(b.tank?.chargeCount ?? 0),
                  render: (_, record) => Number(record.tank?.chargeCount).toLocaleString(),
                },
                {
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  title: '할당일시',
                  align: 'center',
                  width: 170,
                  sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
                  render: (_, record) => moment(record.createdAt, 'YYYYMMDD').format('YYYY-MM-DD HH:mm:ss'),
                },
                {
                  key: 'name',
                  dataIndex: 'name',
                  title: '할당자',
                  width: 96,
                  ellipsis: true,
                  sorter: (a, b) => sortText(a.createdBy?.name, b.createdBy?.name),
                  render: (_, record) => (record.createdBy?.name ? record.createdBy?.name : '-'),
                },
                {
                  key: 'del',
                  dataIndex: 'del',
                  title: '제거',
                  align: 'center',
                  width: 76,
                  render: (_, record) => (
                    <Button
                      disabled={order?.status === 'DELIVERED' || order?.status === 'SHIPPING'}
                      danger
                      type="primary"
                      size="small"
                      onClick={() => deselectTank(record)}>
                      제거
                    </Button>
                  ),
                },
              ]}
              scroll={{ x: 936 }}
            />
          </div>

          <BottomButtons
            left={[
              <Button key={'prevMovePage'} onClick={() => navigate(-1)}>
                뒤로
              </Button>,
            ]}
          />
        </Space>
      </div>

      <Modal
        title="용기 추가"
        open={isOpen}
        width={1280}
        onCancel={() => setIsOpen((isPrev) => !isPrev)}
        maskClosable={false}
        footer={null}>
        <Table
          dataSource={filteredTanks}
          rowKey={'rid'}
          tableLayout="fixed"
          columns={buildTankAddModalColumns({
            getTankUseStatus,
            getTankChargeStatus,
            onAddTank: (tank) => {
              selectTank(tank);
              setIsOpen((isPrev) => !isPrev);
            },
          })}
        />
      </Modal>
    </>
  );
};

export default Page;
